'use client';

import React from 'react';
import './index.css';
import {
  Button,
  Input,
  Link,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router';
import { parseApiErrorWithFields } from '../../utils/errorParser';
import RightImage from '../sign-in/imagepartner.jpg';
import { AcmeIcon } from '../../client/sign-in/acme';
import { ResponsiveModal, Typography } from '../../components';
import { buttonColors } from '../../theme/colors';
import { useTranslations } from '../../contexts/translation.context';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';

// Password strength calculation functions
const calculatePasswordStrength = (password: string) => {
  let strength = 0;
  if (/[a-z]/.test(password)) strength++; // lowercase letter
  if (/[A-Z]/.test(password)) strength++; // uppercase letter
  if (/[0-9]/.test(password)) strength++; // digit
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) strength++; // symbol
  return strength;
};

const getPasswordStrengthColor = (strengthLevel: number) => {
  if (strengthLevel >= 4) return '#19be60'; // Green - strong (all 4 criteria)
  if (strengthLevel >= 3) return '#006fee'; // Blue - good (3 criteria)
  if (strengthLevel >= 2) return '#e8a323'; // Orange - medium (2 criteria)
  if (strengthLevel >= 1) return '#f31260'; // Red - weak (1 criteria)
  return '#d4d4d8'; // Gray - none
};

const getResetPasswordSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    password: Yup.string()
      .min(8, t('auth.validation.passwordRequirements'))
      .max(25, t('auth.validation.passwordRequirements'))
      .matches(/[a-z]/, t('auth.validation.passwordRequirements')) // lowercase letter
      .matches(/[A-Z]/, t('auth.validation.passwordRequirements')) // uppercase letter
      .matches(/[0-9]/, t('auth.validation.passwordRequirements')) // digit
      .matches(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, t('auth.validation.passwordRequirements')) // symbol
      .required(t('auth.validation.passwordRequired')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], t('auth.validation.passwordsMatch'))
      .required(t('auth.validation.confirmPasswordRequired')),
  });

export default function PartnerResetPassword() {
  const { t } = useTranslations();
  const [isVisible, setIsVisible] = React.useState(false);
  const [isConfirmVisible, setIsConfirmVisible] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = React.useState(0);

  const { login, changePasswordPartner } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const toggleVisibility = () => setIsVisible(!isVisible);
  const toggleConfirmVisibility = () => setIsConfirmVisible(!isConfirmVisible);

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      setIsLoading(true);
      setError(null);

      if (!token) {
        setError(t('auth.invalidLinkDescription'));
        return;
      }

      // Call reset password API with token for partner
      await changePasswordPartner({
        token: token,
        password: values.password,
        // confirmPassword: values.confirmPassword
      });
      navigate('/partner/sign-in', {
        state: {
          skipSelect: true,
          message: t('auth.passwordUpdatedSuccess'),
        },
      });
      onOpen(); // Показываем модальное окно
    } catch (error: any) {
      console.error('Password reset failed:', error);
      setError(parseApiErrorWithFields(error, t('auth.errors.resetPasswordError')));
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="relative flex min-h-screen w-full items-center justify-center bg-white px-4 md:px-6 pt-[5.25rem] pb-4 md:py-0">
        <div className="flex flex-col justify-end md:justify-normal fixed top-0 left-0 w-full z-50 bg-white md:bg-transparent border-b border-solid border-neutral-12 px-4 py-6 md:px-12 md:pt-12 md:border-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center justify-center w-[4.75rem] h-[1.375rem] md:w-[7.5rem] md:h-9 shrink-0">
              <AcmeIcon className="size-full" />
            </div>
            <div className="md:hidden">
              <LanguageSwitcher variant="ghost" />
            </div>
          </div>
        </div>

        <div className="flex w-full max-w-[26.5rem] flex-col items-center gap-6 p-4">
          <div className="w-full text-center">
            <p className="pb-2 text-typo-h3-md font-medium leading-10 text-red-600">
              {t('auth.invalidLink')}
            </p>
            <p className="text-base leading-6 text-auth-muted">
              {t('auth.invalidLinkDescription')}
            </p>
          </div>
          <Button
            className="h-12 w-full"
            onClick={() => navigate('/partner/forgot-password')}
            style={{
              backgroundColor: buttonColors.partner.primary.background,
              color: buttonColors.partner.primary.text,
            }}
          >
            {t('auth.requestNewLink')}
          </Button>
        </div>

        <ResponsiveModal
          isOpen={isOpen}
          onClose={() => {
            onClose();
            navigate('/partner/sign-in', { state: { skipSelect: true } });
          }}
          hideCloseButton
        >
          <ModalContent className="md:w-[25rem]">
            <ModalHeader className="flex flex-col gap-1">
              <h3 className="text-lg font-semibold">{t('auth.passwordSaved')}</h3>
            </ModalHeader>
            <ModalBody className="px-4 md:px-6 pb-6 pt-3">
              <p className="text-sm leading-[1.125rem] text-auth-ink">
                {t('auth.passwordUpdatedSuccess')}
              </p>
            </ModalBody>
            <ModalFooter>
              <Button
                onPress={() => {
                  onClose();
                  navigate('/partner/sign-in', { state: { skipSelect: true } });
                }}
                style={{
                  backgroundColor: buttonColors.partner.primary.background,
                  color: buttonColors.partner.primary.text,
                }}
              >
                {t('auth.continue')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ResponsiveModal>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center bg-white px-4 md:px-6 md:gap-12 pt-[5.25rem] pb-4 md:py-0">
      <div className="flex flex-col justify-end md:justify-normal fixed top-0 left-0 w-full z-50 bg-white md:bg-transparent border-b border-solid border-neutral-12 px-4 py-6 md:px-12 md:pt-12 md:border-0">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center justify-center w-[4.75rem] h-[1.375rem] md:w-[7.5rem] md:h-9 shrink-0">
            <AcmeIcon className="size-full" />
          </div>
          <div className="md:hidden">
            <LanguageSwitcher variant="ghost" />
          </div>
        </div>
      </div>

      <div className="flex w-full md:min-h-[calc(100vh-48px)] flex-col justify-center md:flex-row md:items-stretch">
        <div className="flex w-full items-center justify-center bg-background py-6 md:px-6 md:py-12 md:flex-1">
          <div className="flex w-full max-w-[26.5rem] flex-col items-center gap-6 md:gap-8">
            <div className="w-full text-center">
              <Typography variant="h3">{t('auth.newPassword')}</Typography>
            </div>
            {error && (
              <div className="w-full mb-4 bg-auth-error-bg border-2 border-auth-error-border rounded-3 p-4 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="size-10 bg-auth-error-bg border border-auth-error-border rounded-[1.875rem] flex items-center justify-center flex-shrink-0">
                    <Icon
                      icon="mingcute:information-fill"
                      width="19"
                      height="19"
                      className="text-auth-error-text"
                    />
                  </div>
                  <p className="text-auth-error-text text-sm font-medium leading-5">{error}</p>
                </div>
              </div>
            )}
            <Formik
              initialValues={{
                password: '',
                confirmPassword: '',
              }}
              validationSchema={getResetPasswordSchema(t)}
              onSubmit={handleSubmit}
            >
              {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
                <form className="flex w-full flex-col gap-4" onSubmit={handleSubmit} noValidate>
                  {/* Password Field */}
                  <div className="flex flex-col w-full">
                    <Input
                      endContent={
                        <button type="button" onClick={toggleVisibility}>
                          {isVisible ? (
                            <Icon
                              className="pointer-events-none text-2xl text-default-400"
                              icon="solar:eye-closed-linear"
                            />
                          ) : (
                            <Icon
                              className="pointer-events-none text-2xl text-default-400"
                              icon="solar:eye-bold"
                            />
                          )}
                        </button>
                      }
                      label={t('auth.newPassword')}
                      name="password"
                      placeholder={t('placeholders.enterNewPassword')}
                      type={isVisible ? 'text' : 'password'}
                      variant="bordered"
                      value={values.password}
                      onChange={(e) => {
                        const newPassword = e.target.value;
                        setFieldValue('password', newPassword);
                        setPasswordStrength(calculatePasswordStrength(newPassword));
                      }}
                      labelPlacement="inside"
                      className="w-full custom-required-label-partner"
                      classNames={{
                        inputWrapper: 'h-14',
                        input: 'text-black',
                      }}
                    />
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                    <div className="flex w-full gap-1 mt-2">
                      {[1, 2, 3, 4].map((level) => (
                        <div
                          key={level}
                          className="h-1 rounded-full flex-1"
                          style={{
                            backgroundColor:
                              passwordStrength >= level
                                ? getPasswordStrengthColor(passwordStrength)
                                : '#d4d4d8',
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="mt-[-.5rem] text-sm font-regular leading-5 text-auth-muted">
                    {t('auth.passwordHint')}
                  </p>
                  {/* Confirm Password Field */}
                  <div className="flex flex-col w-full">
                    <Input
                      endContent={
                        <button type="button" onClick={toggleConfirmVisibility}>
                          {isConfirmVisible ? (
                            <Icon
                              className="pointer-events-none text-2xl text-default-400"
                              icon="solar:eye-closed-linear"
                            />
                          ) : (
                            <Icon
                              className="pointer-events-none text-2xl text-default-400"
                              icon="solar:eye-bold"
                            />
                          )}
                        </button>
                      }
                      label={t('labels.confirmPassword')}
                      name="confirmPassword"
                      placeholder={t('placeholders.confirmPassword')}
                      type={isConfirmVisible ? 'text' : 'password'}
                      variant="bordered"
                      value={values.confirmPassword}
                      onChange={(e) => setFieldValue('confirmPassword', e.target.value)}
                      labelPlacement="inside"
                      className="w-full custom-required-label-partner"
                      classNames={{
                        inputWrapper: 'h-14',
                        input: 'text-black',
                      }}
                    />
                    <ErrorMessage
                      name="confirmPassword"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <Button
                    className="w-full h-12 mt-4 text-base font-semibold"
                    type="submit"
                    isLoading={isLoading || isSubmitting}
                    disabled={isLoading || isSubmitting}
                    style={{
                      backgroundColor: buttonColors.partner.primary.background,
                      color: buttonColors.partner.primary.text,
                    }}
                  >
                    {isLoading || isSubmitting ? t('auth.resetting') : t('auth.continue')}
                  </Button>
                  <div
                    onClick={() => navigate('/partner/sign-in', { state: { skipSelect: true } })}
                    className="w-full cursor-pointer h-12 bg-auth-surface rounded-2 hover:bg-auth-surface-hover transition-colors user-type-card flex items-center justify-center"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-base font-semibold text-auth-ink">
                        {t('auth.backToLogin')}
                      </span>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>

        <div className="hidden w-full items-stretch gap-12 md:flex md:flex-1">
          <div className="shrink-0">
            <LanguageSwitcher variant="ghost" />
          </div>
          <div
            className="hidden h-full flex-1 overflow-hidden rounded-8 md:block"
            style={{
              backgroundImage: `url(${RightImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        </div>
      </div>
      <ResponsiveModal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          navigate('/partner/sign-in', { state: { skipSelect: true } });
        }}
        hideCloseButton
      >
        <ModalContent className="md:w-[25rem]">
          <ModalHeader className="flex flex-col gap-1">
            <h3 className="text-lg font-semibold">{t('auth.passwordSaved')}</h3>
          </ModalHeader>
          <ModalBody className="px-4 md:px-6 pb-6 pt-3">
            <p className="text-sm leading-[1.125rem] text-auth-ink">
              {t('auth.passwordUpdatedSuccess')}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              onPress={() => {
                onClose();
                navigate('/partner/sign-in', { state: { skipSelect: true } });
              }}
              style={{
                backgroundColor: buttonColors.partner.primary.background,
                color: buttonColors.partner.primary.text,
              }}
            >
              {t('auth.continue')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </ResponsiveModal>
    </div>
  );
}
